import {
    capitalize,
    Fade,
    Stack,
    Typography,
    useTheme
} from "@mui/material";
import Box from "@mui/material/Box";
import React, { useEffect, useState } from "react";
import {CompetitiveGames, CompetitiveTeamRoundSummaries, Team} from "../../../reckon-api";
import {useReferentialContext} from "../../../Context";
import {getNeonColor, scrollable} from "../../../theme";
import TeamIcon from "../../Icons/TeamIcon";
import AgentIcon from "../../Icons/AgentIcon";
import RoundEndIcon from "../../Icons/RoundEndIcon";
import {CreditsIcon} from "../../Icons/GameIcons";
import {NeonCard, NeonCardContent} from "../../../NeonComponents/NeonCards";
import {NeonScoreBadge, NeonLabelBadge} from "../../../NeonComponents/NeonTable";

// Round row component - uses flex to fill width
const RoundRow = ({ 
    round, 
    index, 
    isSelected, 
    onClick 
}: { 
    round: CompetitiveTeamRoundSummaries;
    index: number;
    isSelected: boolean;
    onClick: () => void;
}) => {
    const theme = useTheme();
    const neonColor = getNeonColor(theme);
    
    return (
        <Fade in timeout={100 + index * 30}>
            <Box
                onClick={onClick}
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    width: '100%',
                    padding: '10px 16px',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    minHeight: '48px',
                    background: round.win 
                        ? 'linear-gradient(90deg, rgba(46, 204, 113, 0.15) 0%, rgba(46, 204, 113, 0.04) 100%)'
                        : 'linear-gradient(90deg, rgba(231, 76, 60, 0.15) 0%, rgba(231, 76, 60, 0.04) 100%)',
                    borderLeft: isSelected 
                        ? `4px solid ${neonColor}`
                        : round.win 
                            ? '4px solid rgba(46, 204, 113, 0.5)'
                            : '4px solid rgba(231, 76, 60, 0.5)',
                    boxShadow: isSelected 
                        ? `inset 0 0 20px ${neonColor}20`
                        : 'none',
                    transition: 'all 0.15s ease',
                    '&:hover': {
                        background: round.win 
                            ? 'linear-gradient(90deg, rgba(46, 204, 113, 0.25) 0%, rgba(46, 204, 113, 0.08) 100%)'
                            : 'linear-gradient(90deg, rgba(231, 76, 60, 0.25) 0%, rgba(231, 76, 60, 0.08) 100%)',
                        borderLeft: isSelected 
                            ? `4px solid ${neonColor}`
                            : round.win 
                                ? '4px solid rgba(46, 204, 113, 0.8)'
                                : '4px solid rgba(231, 76, 60, 0.8)',
                    }
                }}
            >
                {/* Round number */}
                <Box sx={{ flex: '0 0 40px', display: 'flex', justifyContent: 'center' }}>
                    <Typography sx={{ 
                        fontSize: '0.95rem', 
                        fontWeight: 700,
                        color: 'rgba(255,255,255,0.9)',
                        fontFamily: 'monospace',
                    }}>
                        {round.round_index}
                    </Typography>
                </Box>
                
                {/* Round end icon */}
                <Box sx={{ flex: '0 0 40px', display: 'flex', justifyContent: 'center' }}>
                    <RoundEndIcon round_end={round.round_end ?? undefined}/>
                </Box>
                
                {/* K-D */}
                <Box sx={{ 
                    flex: '1 1 auto',
                    display: 'flex', 
                    justifyContent: 'center',
                    alignItems: 'center',
                    gap: 0.75,
                }}>
                    <Typography sx={{ fontSize: '0.95rem', fontWeight: 700, color: '#2ecc71' }}>
                        {round.kills}
                    </Typography>
                    <Typography sx={{ fontSize: '0.85rem', color: 'rgba(255,255,255,0.35)' }}>-</Typography>
                    <Typography sx={{ fontSize: '0.95rem', fontWeight: 700, color: '#e74c3c' }}>
                        {round.deaths}
                    </Typography>
                </Box>
                
                {/* Loadout value */}
                <Box sx={{ flex: '1 1 auto', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.75 }}>
                    <CreditsIcon title={'Loadout Value'} color={'2ecc71'}/>
                    <Typography sx={{ 
                        fontSize: '0.85rem', 
                        fontWeight: 600,
                        color: 'rgba(255,255,255,0.8)',
                        fontFamily: 'monospace',
                    }}>
                        {((round.loadout_value ? round.loadout_value : 0) / 1000).toFixed(1)}K
                    </Typography>
                </Box>
                
                {/* Investment */}
                <Box sx={{ flex: '1 1 auto', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.75 }}>
                    <CreditsIcon title={'Invested'} color={'3498db'}/>
                    <Typography sx={{ 
                        fontSize: '0.85rem', 
                        fontWeight: 600,
                        color: 'rgba(255,255,255,0.8)',
                        fontFamily: 'monospace',
                    }}>
                        {((round.investment ? round.investment : 0) / 1000).toFixed(1)}K
                    </Typography>
                </Box>
                
                {/* Bomb site */}
                <Box sx={{ flex: '0 0 40px', display: 'flex', justifyContent: 'center' }}>
                    <Typography sx={{ 
                        fontSize: '0.95rem', 
                        fontWeight: 700,
                        color: round.bomb_site ? 'rgba(255,255,255,0.95)' : 'rgba(255,255,255,0.3)',
                    }}>
                        {round.bomb_site || '-'}
                    </Typography>
                </Box>
            </Box>
        </Fade>
    );
};

// Side section header component
const SideHeader = ({ 
    side, 
    wins, 
    losses 
}: { 
    side: string;
    wins: number;
    losses: number;
}) => {
    const theme = useTheme();
    const neonColor = getNeonColor(theme);
    
    return (
        <Box sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '12px 16px',
            marginTop: '12px',
            marginBottom: '8px',
            borderRadius: '8px',
            background: `linear-gradient(90deg, ${neonColor}12 0%, transparent 100%)`,
            borderLeft: `4px solid ${neonColor}60`,
        }}>
            <Typography sx={{ 
                fontSize: '0.9rem', 
                fontWeight: 700,
                color: 'rgba(255,255,255,0.85)',
                textTransform: 'uppercase',
                letterSpacing: '1px',
            }}>
                {side}
            </Typography>
            <Stack direction="row" spacing={1} alignItems="center">
                <NeonScoreBadge score={wins} variant="win" size="medium" />
                <Typography sx={{ color: 'rgba(255,255,255,0.3)', fontSize: '1rem', fontWeight: 300 }}>:</Typography>
                <NeonScoreBadge score={losses} variant="loss" size="medium" />
            </Stack>
        </Box>
    );
};

// Column headers component
const ColumnHeaders = () => (
    <Box sx={{
        display: 'flex',
        alignItems: 'center',
        padding: '6px 16px 6px 20px', // Account for 4px border in rows
        marginBottom: '4px',
    }}>
        <Typography sx={{ flex: '0 0 40px', fontSize: '0.7rem', color: 'rgba(255,255,255,0.45)', textAlign: 'center', textTransform: 'uppercase', fontWeight: 600, letterSpacing: '0.5px' }}>
            #
        </Typography>
        <Typography sx={{ flex: '0 0 40px', fontSize: '0.7rem', color: 'rgba(255,255,255,0.45)', textAlign: 'center', textTransform: 'uppercase', fontWeight: 600, letterSpacing: '0.5px' }}>
            End
        </Typography>
        <Typography sx={{ flex: '1 1 auto', fontSize: '0.7rem', color: 'rgba(255,255,255,0.45)', textAlign: 'center', textTransform: 'uppercase', fontWeight: 600, letterSpacing: '0.5px' }}>
            K / D
        </Typography>
        <Typography sx={{ flex: '1 1 auto', fontSize: '0.7rem', color: 'rgba(255,255,255,0.45)', textAlign: 'center', textTransform: 'uppercase', fontWeight: 600, letterSpacing: '0.5px' }}>
            Loadout
        </Typography>
        <Typography sx={{ flex: '1 1 auto', fontSize: '0.7rem', color: 'rgba(255,255,255,0.45)', textAlign: 'center', textTransform: 'uppercase', fontWeight: 600, letterSpacing: '0.5px' }}>
            Bought
        </Typography>
        <Typography sx={{ flex: '0 0 40px', fontSize: '0.7rem', color: 'rgba(255,255,255,0.45)', textAlign: 'center', textTransform: 'uppercase', fontWeight: 600, letterSpacing: '0.5px' }}>
            Site
        </Typography>
    </Box>
);

export default function SpecificRoundSelector(props:{ team: Team; game: CompetitiveGames; updateSelection: any; single?: boolean }) {
    const [rounds, setRounds] = React.useState([] as CompetitiveTeamRoundSummaries[]);
    const [selectedIndexes, setSelectedIndex] = React.useState([true] as boolean[]);
    const api = useReferentialContext().reckonApi.competitiveTeamRoundSummaries;
    const ctx = useReferentialContext();
    const theme = useTheme();
    const neonColor = getNeonColor(theme);

    // Helper to get team display name from ID
    const getTeamName = (teamId: string): string => {
        const team = ctx.teamsReferential.find(t => t.id === teamId);
        return team?.name || teamId;
    };

    const handleChange = (index: number) => {
        if(props.single){
            const nsi = rounds.map(i => false);
            nsi[index] = !nsi[index];
            props.updateSelection(
                rounds.filter((round, index) => nsi[index])[0]
            )
            setSelectedIndex(nsi);
        }else{
            const nsi = rounds.map((g, i) => selectedIndexes[i] ? true : false);
            nsi[index] = !nsi[index];
            props.updateSelection(
                rounds.filter((round, index) => nsi[index])
            )
            setSelectedIndex(nsi);
        }
    };

    useEffect(() => {
        setRounds([]);
        api.list({team1: props.team.id, game: props.game.id, ordering: ['round_index']}).then(r => {
            setRounds(r);
            setSelectedIndex([true]);
            props.updateSelection(r[0])
        });
        set_team_agents(props.team.id === props.game.team1 ? props.game.team1_agents : props.game.team2_agents);
        set_vs_team_agents(props.team.id === props.game.team1 ? props.game.team2_agents : props.game.team1_agents);
        set_team_score(props.team.id === props.game.team1 ? props.game.score_team1 : props.game.score_team2);
        set_vs_team_score(props.team.id === props.game.team1 ? props.game.score_team2 : props.game.score_team1);
    }, [props.game, props.team]);

    const [team_agents, set_team_agents] = useState(props.team.id === props.game.team1 ? props.game.team1_agents : props.game.team2_agents);
    const [vs_team_agents, set_vs_team_agents] = useState(props.team.id === props.game.team2 ? props.game.team1_agents : props.game.team2_agents);
    const [team_score, set_team_score] = useState(props.team.id === props.game.team1 ? props.game.score_team1 : props.game.score_team2);
    const [vs_team_score, set_vs_team_score] = useState(props.team.id === props.game.team2 ? props.game.score_team1 : props.game.score_team2);

    const vsTeamId = props.team.id === props.game.team1 ? props.game.team2 : props.game.team1;
    const teamWon = team_score > vs_team_score;

    return (
        <NeonCard
            sx={{
                display: "flex",
                height: "100%",
                width: "420px",
                minWidth: "420px",
                maxWidth: "420px",
                flexDirection:'column'
            }}
        >
            {/* Header section - Match summary */}
            <Box sx={{
                padding: '16px 20px',
                borderBottom: '1px solid rgba(255,255,255,0.08)',
            }}>
                {/* Map name and round count */}
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                    <Typography sx={{ 
                        fontSize: '1rem', 
                        fontWeight: 700,
                        color: neonColor,
                        textTransform: 'uppercase',
                        letterSpacing: '2px',
                    }}>
                        {capitalize(props.game.map)}
                    </Typography>
                    <NeonLabelBadge label={`${rounds.length} Rounds`} maxWidth="100px" />
                </Stack>

                {/* Match score summary */}
                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ px: 1 }}>
                    <Stack alignItems="center" spacing={0.75} sx={{ flex: 1 }}>
                        <TeamIcon team_name={props.team.name} sx={{ width: 40, height: 40 }}/>
                        <Typography sx={{ 
                            fontSize: '0.8rem', 
                            fontWeight: 600,
                            color: teamWon ? '#2ecc71' : 'rgba(255,255,255,0.6)',
                            maxWidth: '90px',
                            textAlign: 'center',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                        }}>
                            {getTeamName(props.team.name)}
                        </Typography>
                    </Stack>

                    <Stack alignItems="center" spacing={0.5}>
                        <Stack direction="row" alignItems="center" spacing={1.5}>
                            <NeonScoreBadge 
                                score={team_score} 
                                variant={teamWon ? 'win' : 'loss'}
                                size="medium"
                            />
                            <Typography sx={{ color: 'rgba(255,255,255,0.3)', fontSize: '1.25rem', fontWeight: 300 }}>:</Typography>
                            <NeonScoreBadge 
                                score={vs_team_score} 
                                variant={!teamWon ? 'win' : 'loss'}
                                size="medium"
                            />
                        </Stack>
                    </Stack>

                    <Stack alignItems="center" spacing={0.75} sx={{ flex: 1 }}>
                        <TeamIcon team_name={vsTeamId} sx={{ width: 40, height: 40 }}/>
                        <Typography sx={{ 
                            fontSize: '0.8rem', 
                            fontWeight: 600,
                            color: !teamWon ? '#2ecc71' : 'rgba(255,255,255,0.6)',
                            maxWidth: '90px',
                            textAlign: 'center',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                        }}>
                            {getTeamName(vsTeamId)}
                        </Typography>
                    </Stack>
                </Stack>
            </Box>

            {/* Team compositions - both on same row */}
            <Box sx={{ 
                padding: '14px 20px',
                borderBottom: '1px solid rgba(255,255,255,0.08)',
            }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                    {/* Team agents */}
                    <Stack direction="row" alignItems="center" spacing={0.5}>
                        <TeamIcon team_name={props.team.name} sx={{ width: 24, height: 24 }}/>
                        {team_agents.split(',').map((agent_name: string, idx: number) => (
                            <AgentIcon key={idx} agent_name={agent_name} sx={{ width: 24, height: 24 }}/>
                        ))}
                    </Stack>
                    
                    <Typography sx={{ color: 'rgba(255,255,255,0.25)', fontSize: '0.85rem', fontWeight: 500 }}>vs</Typography>
                    
                    {/* VS team agents */}
                    <Stack direction="row" alignItems="center" spacing={0.5}>
                        {vs_team_agents.split(',').map((agent_name: string, idx: number) => (
                            <AgentIcon key={idx} agent_name={agent_name} sx={{ width: 24, height: 24 }}/>
                        ))}
                        <TeamIcon team_name={vsTeamId} sx={{ width: 24, height: 24 }}/>
                    </Stack>
                </Stack>
            </Box>

            {/* Rounds content */}
            <NeonCardContent sx={{ height:'100%', width:'100%', flexDirection:'column', padding: '12px 16px', ...scrollable }}>
                <Stack direction={'column'} sx={{ width:'100%' }} spacing={0.75}>
                    {/* First half */}
                    {rounds.length >= 1 && (
                        <>
                            <SideHeader 
                                side={rounds[0].side || 'First Half'} 
                                wins={rounds.filter(r => r.side === rounds[0].side).reduce((sum, r) => sum + (r.win ? 1 : 0), 0)}
                                losses={rounds.filter(r => r.side === rounds[0].side).reduce((sum, r) => sum + (r.win ? 0 : 1), 0)}
                            />
                            <ColumnHeaders />
                            {rounds.slice(0, 12).map((round, index) => (
                                <RoundRow
                                    key={round.round_index || index}
                                    round={round}
                                    index={index}
                                    isSelected={selectedIndexes[index] === true}
                                    onClick={() => handleChange(index)}
                                />
                            ))}
                        </>
                    )}

                    {/* Second half */}
                    {rounds.length >= 12 && (
                        <>
                            <SideHeader 
                                side={rounds[12].side || 'Second Half'} 
                                wins={rounds.filter(r => r.side === rounds[12].side).reduce((sum, r) => sum + (r.win ? 1 : 0), 0)}
                                losses={rounds.filter(r => r.side === rounds[12].side).reduce((sum, r) => sum + (r.win ? 0 : 1), 0)}
                            />
                            <ColumnHeaders />
                            {rounds.slice(12, rounds.length).map((round, index) => (
                                <RoundRow
                                    key={round.round_index || (index + 12)}
                                    round={round}
                                    index={index + 12}
                                    isSelected={selectedIndexes[index + 12] === true}
                                    onClick={() => handleChange(index + 12)}
                                />
                            ))}
                        </>
                    )}

                    {/* Empty state */}
                    {rounds.length === 0 && (
                        <Box sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            height: '200px',
                        }}>
                            <Typography sx={{ color: 'rgba(255,255,255,0.4)', fontStyle: 'italic', fontSize: '0.95rem' }}>
                                Loading rounds...
                            </Typography>
                        </Box>
                    )}
                </Stack>
            </NeonCardContent>
        </NeonCard>
    );
}
